import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchBar } from '@/components/ui/search-bar';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { EventAttendanceContent } from '@/content/EventAttendanceContent';
import { formatEventTimeRange } from '@/lib/utils/events.utils';
import type {
  EventAttendanceProps,
  AttendeeListItem,
  AttendanceTabTriggerProps,
  AttendeeListProps,
} from '@/lib/types/attendance.types';
import { MapPinIcon, ClockIcon, PlusIcon } from '@phosphor-icons/react';
import { getInitials } from '@/lib/utils/name.utils';
import { UserListRow } from '@/components/UserListRow';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { EditEventContent } from '@/content/EditEventContent';
import { useDeleteEvent } from '@/lib/hooks/useEvents';
import { EventActionsDrawer } from '@/components/events/EventActionsDrawer';
import { AttendeeDrawer } from '@/components/events/AttendeeDrawer';
import { useDeleteAttendance } from '@/lib/hooks/useAttendance';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { listGroupMembershipsForUsers } from '@/lib/services/memberships';
import {
  buildGroupMembershipsByUserId,
  generateAttendanceCSV,
  downloadCSV,
} from '@/lib/utils/export.utils';

export function EventAttendance({
  event,
  rsvps,
  attendance,
  initialTab = 'going',
}: EventAttendanceProps) {
  useRoleDirectory();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [isActionsDrawerOpen, setIsActionsDrawerOpen] = useState(false);
  const [isDeleteDrawerOpen, setIsDeleteDrawerOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] =
    useState<AttendeeListItem | null>(null);
  const [isAttendeeDrawerOpen, setIsAttendeeDrawerOpen] = useState(false);
  const deleteEvent = useDeleteEvent();
  const deleteAttendance = useDeleteAttendance();
  const { toast, showToast, hideToast } = useToastNotification();
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const goingRsvps = useMemo(
    () => rsvps.filter((rsvp) => rsvp.status === 'yes'),
    [rsvps]
  );
  const notGoingRsvps = useMemo(
    () => rsvps.filter((rsvp) => rsvp.status === 'no'),
    [rsvps]
  );
  const attendanceEntries = attendance;

  const goingDisplay = useMemo(
    () =>
      goingRsvps.map((attendee) => {
        const name =
          attendee.profile?.full_name || attendee.profile?.email || 'Member';
        return {
          key: `${attendee.event_id}-${attendee.user_id}-${attendee.id}`,
          name,
          initials: getInitials(name),
          email: attendee.profile?.email || null,
          role: attendee.profile?.role || null,
          userId: attendee.user_id,
        };
      }),
    [goingRsvps]
  );

  const notGoingDisplay = useMemo(
    () =>
      notGoingRsvps.map((attendee) => {
        const name =
          attendee.profile?.full_name || attendee.profile?.email || 'Member';
        return {
          key: `${attendee.event_id}-${attendee.user_id}-${attendee.id}`,
          name,
          initials: getInitials(name),
          email: attendee.profile?.email || null,
          role: attendee.profile?.role || null,
          userId: attendee.user_id,
        };
      }),
    [notGoingRsvps]
  );

  const attendanceDisplay = useMemo(
    () =>
      attendanceEntries.map((record) => {
        const name =
          record.profile?.full_name || record.profile?.email || 'Member';
        return {
          key: `${record.event_id}-${record.user_id}-${record.id}`,
          name,
          initials: getInitials(name),
          email: record.profile?.email || null,
          role: record.profile?.role || null,
          userId: record.user_id,
          attendanceId: record.id,
        };
      }),
    [attendanceEntries]
  );

  const attendanceCount = attendanceEntries.length;

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchQuery('');
  };

  const filterBySearch = (list: AttendeeListItem[]): AttendeeListItem[] => {
    if (!searchQuery.trim()) {
      return list;
    }
    const query = searchQuery.toLowerCase();
    return list.filter((attendee) =>
      attendee.name.toLowerCase().includes(query)
    );
  };

  const handleAddAttendee = () => {
    navigate({
      to: '/events/$eventId/attendance/add',
      params: { eventId: event.id.toString() },
      search: { tab: 'attendance' },
    });
  };

  const handleEditEvent = () => {
    navigate({
      to: '/events/$eventId/edit',
      params: { eventId: event.id.toString() },
    });
    setIsActionsDrawerOpen(false);
  };

  const handleDeleteEventClick = () => {
    setIsActionsDrawerOpen(false);
    setIsDeleteDrawerOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEvent.mutateAsync(event.id);
      setIsDeleteDrawerOpen(false);
      // Show success toast
      showToast({
        title: 'Event deleted',
        description: 'The event has been deleted successfully.',
        variant: 'success',
      });
      navigate({ to: '/events' });
    } catch (error) {
      console.error('Failed to delete event', error);
      // Show error toast
      showToast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete event. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleAttendeeClick = (attendee: AttendeeListItem) => {
    setSelectedAttendee(attendee);
    setIsAttendeeDrawerOpen(true);
  };

  const handleRemoveAttendance = async () => {
    if (!selectedAttendee?.attendanceId) {
      return;
    }
    try {
      await deleteAttendance.mutateAsync({
        attendanceId: selectedAttendee.attendanceId,
        eventId: event.id,
      });
      // Show success toast
      showToast({
        title: 'Attendance removed',
        description: `${selectedAttendee.name}'s attendance has been removed.`,
        variant: 'success',
      });
      setIsAttendeeDrawerOpen(false);
      setSelectedAttendee(null);
    } catch (error) {
      console.error('Failed to remove attendance', error);
      // Show error toast
      showToast({
        title: 'Remove failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to remove attendance. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleExport = async () => {
    try {
      const userIds = Array.from(
        new Set(
          [...rsvps, ...attendance]
            .map((record) => record.user_id)
            .filter((userId) => userId.trim() !== '')
        )
      );
      const memberships = await listGroupMembershipsForUsers(userIds);
      const membershipsByUserId = buildGroupMembershipsByUserId(memberships);
      const csvContent = generateAttendanceCSV(
        event,
        rsvps,
        attendance,
        membershipsByUserId
      );
      const filename = `${event.title.replace(/[^a-z0-9]/gi, '_')}_attendance_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Failed to export attendance', error);
    }
  };

  return (
    <>
      <AttendeeDrawer
        open={isAttendeeDrawerOpen}
        onOpenChange={setIsAttendeeDrawerOpen}
        attendee={selectedAttendee}
        showRemoveButton={activeTab === 'attendance'}
        onRemove={handleRemoveAttendance}
        isRemoving={deleteAttendance.isPending}
      />
      <ActionDrawer
        open={isDeleteDrawerOpen}
        onOpenChange={setIsDeleteDrawerOpen}
        title={EditEventContent.deleteDrawer.title}
        description={EditEventContent.deleteDrawer.message}
        confirmLabel={EditEventContent.deleteDrawer.deleteButton}
        cancelLabel={EditEventContent.deleteDrawer.cancelButton}
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteEvent.isPending}
        loadingLabel="Deleting..."
      />
      <div className="w-full max-w-4xl mx-auto bg-slate-700">
        <div className="px-4 pb-6 space-y-2">
          <header>
            <h1 className="text-[36px]">{event.title}</h1>
          </header>

          <section className="rounded-md bg-slate-600 px-3 py-5 space-y-1">
            {event.location && (
              <div className="flex items-start gap-1 text-body text-slate-50">
                <MapPinIcon
                  size={24}
                  weight="regular"
                  className="flex-shrink-0"
                />
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                  {event.location}
                </span>
              </div>
            )}
            <div className="flex items-center gap-1 text-body text-slate-50">
              <ClockIcon size={24} weight="regular" />
              <span>
                {formatEventTimeRange(event.start_time, event.end_time)}
              </span>
            </div>
            <div className="flex items-center gap-3 text-body text-slate-50">
              <span className="h-3 w-3 ml-2 rounded-full bg-green-400" />
              <span>{goingRsvps.length} Attending</span>
            </div>
          </section>

          <section className="bg-slate-700 rounded-3xl py-4">
            <div className="flex items-center gap-3">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by name..."
                className="flex-1"
              />
              <EventActionsDrawer
                open={isActionsDrawerOpen}
                onOpenChange={setIsActionsDrawerOpen}
                onEdit={handleEditEvent}
                onDelete={handleDeleteEventClick}
                onExport={handleExport}
                isDeleting={deleteEvent.isPending}
              />
            </div>

            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="mt-4"
            >
              <TabsList className="flex w-full items-center justify-between gap-4 bg-transparent p-0">
                <AttendanceTabTrigger
                  value="going"
                  label={EventAttendanceContent.tabs.going}
                  count={goingRsvps.length}
                  accentClasses="bg-green-400"
                />
                <AttendanceTabTrigger
                  value="notGoing"
                  label={EventAttendanceContent.tabs.notGoing}
                  count={notGoingRsvps.length}
                  accentClasses="bg-red-400"
                />
                <AttendanceTabTrigger
                  value="attendance"
                  label={EventAttendanceContent.tabs.attendance}
                  count={attendanceCount}
                  accentClasses="bg-sky-400"
                />
              </TabsList>

              <TabsContent value="going">
                <div className="bg-slate-600 mt-4">
                  <AttendeeList
                    attendees={filterBySearch(goingDisplay)}
                    emptyState={EventAttendanceContent.emptyStates.going}
                    onAttendeeClick={handleAttendeeClick}
                  />
                </div>
              </TabsContent>

              <TabsContent value="notGoing">
                <div className="bg-slate-600 mt-4">
                  <AttendeeList
                    attendees={filterBySearch(notGoingDisplay)}
                    emptyState={EventAttendanceContent.emptyStates.notGoing}
                    onAttendeeClick={handleAttendeeClick}
                  />
                </div>
              </TabsContent>

              <TabsContent value="attendance">
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleAddAttendee}
                    className="w-full flex rounded-md items-center gap-2 px-6 py-3 bg-purple-400 text-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-200"
                  >
                    <PlusIcon size={20} className="text-slate-100" />
                    <span className="text-large text-slate-100">
                      {EventAttendanceContent.buttons.addAttendee}
                    </span>
                  </button>
                  <AttendeeList
                    attendees={filterBySearch(attendanceDisplay)}
                    emptyState={EventAttendanceContent.emptyStates.attendance}
                    onAttendeeClick={handleAttendeeClick}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </section>
        </div>
      </div>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}

function AttendanceTabTrigger({
  value,
  label,
  count,
  accentClasses,
}: AttendanceTabTriggerProps) {
  return (
    <TabsTrigger
      value={value}
      className="flex-1 justify-center gap-2 px-0 py-2 text-slate-400 border-b-2 border-transparent data-[state=active]:text-slate-50 data-[state=active]:border-purple-400 transition-colors"
    >
      <span
        className={`rounded-lg px-3 text-slate-50 text-small ${accentClasses}`}
      >
        {count}
      </span>
      <span
        className="text-large"
        style={{ fontSize: 'clamp(14px, 4vw, 18px)' }}
      >
        {label}
      </span>
    </TabsTrigger>
  );
}

function AttendeeList({
  attendees,
  emptyState,
  onAttendeeClick,
}: AttendeeListProps) {
  if (attendees.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-500 px-4 py-8 text-center text-body text-slate-300 mt-3">
        {emptyState}
      </div>
    );
  }

  return (
    <ul className="bg-slate-600 mt-4 rounded-xl">
      {attendees.map((attendee) => (
        <UserListRow
          key={attendee.key}
          name={attendee.name}
          initials={attendee.initials}
          onClick={
            onAttendeeClick ? () => onAttendeeClick(attendee) : undefined
          }
        />
      ))}
    </ul>
  );
}
